/**
 * GB #1612 -- Codex P2 (second review): the storefront banner container
 * (SellerProfileHeader.tsx) is FIXED-HEIGHT with flexible width, not a
 * fixed aspect ratio: 200px tall below the 640px breakpoint, 300px tall at
 * 640px and up (capped at 1320px wide). Its visible aspect ratio therefore
 * varies continuously with viewport width -- ~1.6:1 to ~3.2:1 on phones,
 * ~2.13:1 at a 640px tablet, ~3.41:1 at 1024px, only reaching the 4.4:1
 * ratio at the 1320px cap.
 *
 * The Shop Settings banner preview exists specifically so sellers see the
 * crop buyers will actually see. A fixed aspect-ratio box (this repo's
 * first attempt) is WRONG at every width except exactly 1320px+ -- it
 * showed sellers a substantially different crop than the real storefront
 * at the common 640-1024px range. This constant is the single source of
 * truth for both components' container height classes, so they cannot
 * silently drift apart the way the aspect-ratio version did.
 *
 * No Firebase or other heavy imports here on purpose -- SellerProfileHeader
 * currently has zero Firebase dependency (verified: a real Playwright
 * network trace against its exact rendered markup was possible only
 * because of that), and this file must stay safe for it to import.
 */
export const STOREFRONT_BANNER_HEIGHT_CLASSES = 'h-[20rem] sm:h-[30rem]';
export const STOREFRONT_BANNER_MAX_WIDTH_CLASS = 'max-w-[1320px]';

export const STOREFRONT_BANNER_MOBILE_BREAKPOINT_PX = 640;
export const STOREFRONT_BANNER_MAX_WIDTH_PX = 1320;
export const STOREFRONT_BANNER_MOBILE_HEIGHT_PX = 200;
export const STOREFRONT_BANNER_DESKTOP_HEIGHT_PX = 300;

/**
 * GB #1612 -- Codex P2 (fourth review): STOREFRONT_BANNER_HEIGHT_CLASSES
 * alone is not sufficient for a preview that renders in a NARROWER column
 * than the real storefront -- e.g. the Shop Settings dashboard page,
 * where DashboardLayout.tsx subtracts a 26rem side menu plus
 * lg:px-default-padding-x (12rem each side) from the available width, so
 * the settings column is never actually as wide as the buyer's viewport.
 * Sharing only the height classes there reproduced the right HEIGHT but
 * the wrong WIDTH, which still yields the wrong object-cover crop (verified
 * by real-browser measurement: at a 1024px viewport the storefront
 * container measures 1024x300 = 3.41:1, while a same-height dashboard
 * preview measured only 524x300 = 1.75:1 -- a materially different crop).
 *
 * object-cover's crop is a function of the ASPECT RATIO mismatch between
 * container and source image, not of the container's absolute pixel size.
 * Two containers with the SAME ratio but different absolute widths show
 * the IDENTICAL proportional slice of the source image, just scaled --
 * confirmed by the same real-browser measurement: applying this function's
 * output as the preview's aspect-ratio at a 524px-wide column produced
 * exactly 3.41:1, matching the 1024px-wide storefront's ratio precisely,
 * even though the two boxes are physically different sizes.
 *
 * Any preview that cannot occupy the storefront's actual width must
 * therefore compute and apply THIS ratio (via CSS aspect-ratio) rather
 * than copying the storefront's fixed-height classes directly.
 */
export function getStorefrontBannerAspectRatio(viewportWidthPx: number): number {
  const effectiveWidth = Math.min(
    viewportWidthPx,
    STOREFRONT_BANNER_MAX_WIDTH_PX
  );
  const effectiveHeight =
    viewportWidthPx < STOREFRONT_BANNER_MOBILE_BREAKPOINT_PX
      ? STOREFRONT_BANNER_MOBILE_HEIGHT_PX
      : STOREFRONT_BANNER_DESKTOP_HEIGHT_PX;
  return effectiveWidth / effectiveHeight;
}

// GB #1612 -- Codex fifth review, finding 12: the desktop preview's ratio
// was computed straight from the seller's OWN current viewport, with no
// floor. A seller reviewing their desktop banner copy ("This is what
// buyers see on a computer or tablet") from their own PHONE got the MOBILE
// ratio (1.95) -- rendering identically to the phone preview sitting right
// below it, contradicting its own label. This preview specifically
// represents a desktop/tablet buyer regardless of which device the seller
// is using to view Shop Settings, so its input width must never go below
// this floor.
export const DESKTOP_PREVIEW_MIN_REFERENCE_WIDTH_PX = 1024;

// GB #1612 -- Codex fifth review, finding 11: the phone preview
// (ShopSettingsPage.tsx) hardcoded a literal aspect-[39/20] Tailwind class
// instead of deriving from this file -- the exact drift this file's own
// module comment says it exists to prevent, in the single most important
// preview of a mobile-first feature. 390/200, matching the storefront's
// real sub-640px container (STOREFRONT_BANNER_MOBILE_HEIGHT_PX) at the
// established 390px phone reference width.
export const PHONE_PREVIEW_REFERENCE_WIDTH_PX = 390;
export const PHONE_PREVIEW_ASPECT_RATIO =
  PHONE_PREVIEW_REFERENCE_WIDTH_PX / STOREFRONT_BANNER_MOBILE_HEIGHT_PX;

// GB #1612 -- Codex fifth review, finding 13a: Tailwind's `sm:` prefix is
// `min-width: 640px` (640px itself IS "sm"), so the true "not sm" range is
// [0, 639.999...]. The <picture><source media="(max-width: 639px)"> in
// SellerProfileHeader.tsx used a bare 639px, which excludes the [639, 640)
// sub-pixel band entirely -- at browser zoom or OS display scaling, a
// container can land in that band using the mobile HEIGHT class (since
// sm: has not triggered) while the media query ALSO fails to match (since
// width > 639), so the browser falls through to the desktop <img> in a box
// sized for the mobile crop. 639.98px is the standard community-established
// value for authoring a max-width query that is the true complement of a
// 640px min-width breakpoint, accounting for this rounding gap.
export const STOREFRONT_BANNER_MOBILE_MEDIA_QUERY = '(max-width: 639.98px)';
